// ─────────────────────────────────────────
// ResetPassword.js — set a new password
// ─────────────────────────────────────────
// User lands here by clicking the link in the
// password reset email. Supabase auto-detects
// the token in the URL and establishes a
// temporary session. We just show the new
// password form and call updateUser().

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function ResetPassword() {
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [ready, setReady] = useState(false) // true once Supabase confirms recovery session

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when it detects the reset token in the URL hash.
    // We wait for that before showing the form so we know the session is valid.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setReady(true)
      }
    })

    // Also check if there's already a valid session (user may have landed here mid-session)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true)
    })

    return () => subscription.unsubscribe()
  }, [])

  async function handleReset(e) {
    e.preventDefault()
    setError(null)

    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirm) {
      setError("Passwords don't match.")
      return
    }

    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })

    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      setDone(true)
      // Sign out the recovery session so they log in fresh with the new password
      await supabase.auth.signOut()
      setTimeout(() => navigate('/login'), 2500)
    }
  }

  // ── Success screen ──
  if (done) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>BREAKIE</div>
          <div style={s.successIcon}>🔐</div>
          <div style={s.successTitle}>Password updated!</div>
          <div style={s.successBody}>
            Your new password is set. Redirecting you to log in...
          </div>
        </div>
      </div>
    )
  }

  // ── Waiting for Supabase to confirm the recovery token ──
  if (!ready) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>BREAKIE</div>
          <div style={s.tagline}>Verifying your reset link...</div>
          <div style={s.spinner}>⛳</div>
        </div>
      </div>
    )
  }

  // ── Reset password form ──
  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>BREAKIE</div>
        <div style={s.tagline}>Choose a new password</div>

        <form onSubmit={handleReset} style={s.form}>
          <input
            style={s.input}
            type="password"
            placeholder="New password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoFocus
            minLength={6}
          />
          <input
            style={s.input}
            type="password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
            minLength={6}
          />

          <div style={s.hint}>Minimum 6 characters.</div>

          {error && <div style={s.error}>{error}</div>}

          <button
            style={{ ...s.btn, opacity: loading ? 0.6 : 1 }}
            type="submit"
            disabled={loading}>
            {loading ? 'Saving...' : 'Set New Password →'}
          </button>
        </form>
      </div>
    </div>
  )
}

const s = {
  page: {
    minHeight: '100vh', background: '#0e1f13',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, fontFamily: "'Barlow', sans-serif"
  },
  card: {
    background: '#1e3325', borderRadius: 24, padding: '48px 36px',
    width: '100%', maxWidth: 400, textAlign: 'center'
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 42, letterSpacing: 4, color: '#c8f135', marginBottom: 8
  },
  tagline: { fontSize: 14, color: '#6aad80', marginBottom: 36 },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  input: {
    width: '100%', background: '#0e1f13', border: '1.5px solid #2a4a2e',
    borderRadius: 12, padding: '14px 16px', fontSize: 15, color: 'white',
    outline: 'none', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  },
  hint: { fontSize: 12, color: '#4a7a56', textAlign: 'left', marginTop: -4 },
  error: {
    background: 'rgba(232,64,64,0.15)', border: '1px solid rgba(232,64,64,0.3)',
    color: '#f87171', borderRadius: 10, padding: '10px 14px', fontSize: 13
  },
  btn: {
    background: '#c8f135', color: '#0e1f13', border: 'none', borderRadius: 12,
    padding: '16px', fontSize: 16, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    cursor: 'pointer', marginTop: 4
  },
  successIcon: { fontSize: 44, margin: '0 0 12px' },
  successTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135',
    letterSpacing: 0.5, marginBottom: 12
  },
  successBody: { fontSize: 14, color: '#6aad80', lineHeight: 1.6 },
  spinner: { fontSize: 36, marginTop: 16, animation: 'spin 1s linear infinite' }
}
